import { DEFAULT_LOCALE } from '../locales/index.js';
import { formatUsd } from './currency.js';
import { formatterLocale, intlFormatter, resetFormatterCache } from './intl.js';

export { formatterLocale, intlFormatter, resetFormatterCache };

/**
 * Locale-aware formatters (ENG-378) — the one place dates, times, numbers,
 * lists and money are rendered for the active locale.
 *
 * Components take them from `useLocale().format` (or `useFormatters()`), so
 * every value on a page follows the language the listener chose, with the
 * same `Intl` options the former `toLocaleDateString(locale, …)` calls used —
 * en-US output is byte-identical to what it replaces. Helpers called without
 * a component (`notifications.js`) build their own with `createFormatters`.
 *
 *   const { t, format } = useLocale();
 *   format.date(row.createdAt, 'short')        → "Sep 15, 2026"      ('' when unparseable)
 *   format.time(show.startsAt)                 → "8:00 PM"
 *   format.number(viewers)                     → "1,234"
 *   format.list(['Ada', 'Grace'])              → "Ada and Grace"
 *   format.relativeTime(readAt, { now })       → "yesterday"
 *   format.country('GB')                       → "United Kingdom"    (ENG-375)
 *   format.usd(5.5)                            → "$5.50"             (see currency.js)
 *
 * Missing, null or unparseable input formats to '' rather than throwing or
 * printing "Invalid Date" / "NaN", so a caller's own fallback reads as
 * `format.date(value) || t('common.status.unknown')`.
 *
 * `Intl` instances are cached per locale and options (`./intl.js`) and
 * shared by every formatter, `formatUsd` included.
 */

/** Presets for `format.date(value, style)`; any `Intl.DateTimeFormat` options object works too. */
export const DATE_STYLES = Object.freeze({
  /** 9/15/2026 — what `toLocaleDateString(locale)` gives without options. */
  numeric: Object.freeze({ year: 'numeric', month: 'numeric', day: 'numeric' }),
  /** Sep 15, 2026 */
  short: Object.freeze({ year: 'numeric', month: 'short', day: 'numeric' }),
  /** September 2026 */
  monthYear: Object.freeze({ year: 'numeric', month: 'long' }),
  /** Tue */
  weekday: Object.freeze({ weekday: 'short' }),
  /** Sep 15 */
  monthDay: Object.freeze({ month: 'short', day: 'numeric' }),
  /** Tue, Sep 15 */
  weekdayMonthDay: Object.freeze({ weekday: 'short', month: 'short', day: 'numeric' }),
});

/** Presets for `format.time(value, style)`. */
export const TIME_STYLES = Object.freeze({
  /** 8:00 PM */
  short: Object.freeze({ hour: 'numeric', minute: '2-digit' }),
});

/** Presets for `format.dateTime(value, style)`. */
export const DATE_TIME_STYLES = Object.freeze({
  /** Sep 15, 2026, 8:00 PM */
  short: Object.freeze({ dateStyle: 'medium', timeStyle: 'short' }),
  /** Tue, Sep 15, 8:00 PM PDT — a show's start, zone included because shows are announced in the band's zone. */
  showStart: Object.freeze({ weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }),
  /** Sep 25, 2026, 8:00 PM PDT — an event in a band's list, in the viewer's zone and saying which (ENG-511). */
  eventListing: Object.freeze({ year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }),
});

/**
 * An ISO 3166-1 alpha-2 region subtag, the only thing `Intl.DisplayNames`
 * will name as a country. Kept here rather than imported from a feature
 * helper: this is the formatter's input contract, not any one payload's.
 */
const REGION_CODE = /^[A-Z]{2}$/;

/** Relative-time units, largest first, with the span below which the next smaller one is used. */
const RELATIVE_UNITS = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
  ['second', 1],
];

/* ── Input normalisation ───────────────────────────────────────────────── */

/** A valid `Date` from a `Date`, timestamp or date string; null for anything else, including "Invalid Date". */
export const toDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

/** A preset name → its options; an options object → itself; nothing → the default preset. */
const styleOptions = (presets, style, fallback) => {
  if (style === undefined || style === null) return presets[fallback];
  if (typeof style === 'string') {
    if (!(style in presets)) throw new TypeError(`Unknown format style "${style}" (one of ${Object.keys(presets).join(', ')})`);
    return presets[style];
  }
  return style;
};

/* ── Formatters ────────────────────────────────────────────────────────── */

/**
 * Formatters bound to `locale`. `timeZone`, when given, applies to every
 * date and time that does not name its own — a page that shows one band's
 * schedule in the band's zone; by default the viewer's zone applies.
 */
export const createFormatters = (locale = DEFAULT_LOCALE, { timeZone } = {}) => {
  const tag = formatterLocale(locale);

  const dateTimeFormatter = (options) =>
    intlFormatter(Intl.DateTimeFormat, tag, timeZone && !options.timeZone ? { ...options, timeZone } : options);

  const formatDateLike = (value, options) => {
    const date = toDate(value);
    return date ? dateTimeFormatter(options).format(date) : '';
  };

  return Object.freeze({
    locale: tag,
    timeZone,

    /** A calendar date; `style` is a `DATE_STYLES` name (default `numeric`) or `Intl.DateTimeFormat` options. */
    date: (value, style) => formatDateLike(value, styleOptions(DATE_STYLES, style, 'numeric')),

    /** A clock time; `style` is a `TIME_STYLES` name (default `short`) or `Intl.DateTimeFormat` options. */
    time: (value, style) => formatDateLike(value, styleOptions(TIME_STYLES, style, 'short')),

    /** Date and time together; `style` is a `DATE_TIME_STYLES` name (default `short`) or options. */
    dateTime: (value, style) => formatDateLike(value, styleOptions(DATE_TIME_STYLES, style, 'short')),

    /** A number in the locale's digits and grouping — what `toLocaleString(locale)` gave; '' unless finite. */
    number: (value, options) => (isFiniteNumber(value) ? intlFormatter(Intl.NumberFormat, tag, options).format(value) : ''),

    /** A ratio (0.25 → "25%"); '' unless finite. */
    percent: (value, options) =>
      isFiniteNumber(value) ? intlFormatter(Intl.NumberFormat, tag, { style: 'percent', ...options }).format(value) : '',

    /**
     * "3 hours ago" / "in 2 days" / "yesterday" for `value` relative to
     * `now` (default: the current time), in the largest unit the distance
     * fills, rounded down like the rest of the app's relative times (59.6
     * minutes is "59 minutes ago", not "60"); `numeric: 'auto'` lets the
     * locale say "yesterday" and "now". '' when either date is unparseable.
     */
    relativeTime: (value, { now = Date.now(), numeric = 'auto', style = 'long' } = {}) => {
      const date = toDate(value);
      const reference = toDate(now);
      if (!date || !reference) return '';
      const seconds = (date.getTime() - reference.getTime()) / 1000;
      const magnitude = Math.abs(seconds);
      const [unit, span] = RELATIVE_UNITS.find(([, unitSpan]) => magnitude >= unitSpan) ?? RELATIVE_UNITS[RELATIVE_UNITS.length - 1];
      const amount = Math.trunc(seconds / span);
      return intlFormatter(Intl.RelativeTimeFormat, tag, { numeric, style }).format(amount === 0 ? 0 : amount, unit);
    },

    /**
     * Items joined the way the locale writes a list ("Ada, Grace, and Mary";
     * `type: 'unit'` for a bare "a, b, c"). Empty entries are skipped and
     * the rest rendered as text.
     */
    list: (items, options) => {
      const entries = Array.from(items ?? [], (item) => (item == null ? '' : String(item))).filter(Boolean);
      if (typeof Intl.ListFormat !== 'function') return entries.join(', ');
      return intlFormatter(Intl.ListFormat, tag, options).format(entries);
    },

    /**
     * A country named in the locale's language (ENG-375): `'GB'` → "United
     * Kingdom", "Royaume-Uni". The code itself when this runtime has no name
     * for it (a valid but unassigned region, an ICU build without the data),
     * and `''` for anything that is not a region code — so a caller's own
     * fallback reads `format.country(code) || t('….yourCountry')`.
     */
    country: (code) => {
      const region = typeof code === 'string' ? code.trim().toUpperCase() : '';
      if (!REGION_CODE.test(region)) return '';
      if (typeof Intl.DisplayNames !== 'function') return region;
      try {
        // `fallback: 'code'` is the default, said out loud: the alternative
        // returns undefined for a code CLDR has no name for.
        return intlFormatter(Intl.DisplayNames, tag, { type: 'region', fallback: 'code' }).of(region) || region;
      } catch {
        return region;
      }
    },

    /** A US-dollar amount in the locale's notation; see `formatUsd` for `cents`. */
    usd: (amount, options) => formatUsd(amount, tag, options),
  });
};

/**
 * Formatters bound to the English source locale, for helpers called without
 * a component's `format` (tests, non-React call sites) — the counterpart of
 * `englishT`. Components and hooks use `useFormatters()` instead so the
 * active locale applies.
 */
export const englishFormat = createFormatters(DEFAULT_LOCALE);
